/**
 * 多版本菜单（N35-B2 第 4 轮 + B5 review R4B5-4）：两个分享码 —— 菜单预览码 `previewMenu`（B6）、点单链接码 `menuLink`（B5）—— 不送给第三方。
 *
 * 两个码都只活在地址里；码本来就印在桌上 / 发给店长，plan §3.8 接受自托管的 Umami / OpenReplay「照常统计」。
 * 第三方 SaaS 没有权衡过，这里把送出这台设备的地址去掉两个码（别的参数逐字节不动）：
 * - Google Analytics：page_view 的 page_location（`gtagPageView`，main.js 给 vue-gtag 的 `pageTrackerTemplate`），
 *   以及之后自定义事件（view_cart / begin_checkout / purchase / 换语言 / 性能监控 …）按页面记的地址（`keepShareCodesOutOfGtag`：
 *   每次路由切换后 `gtag('set', { page_location })`）；`utils/performance.js` 上报的 url；
 *   还有 page_referrer（B25 复核 Codex P1 / Claude P2）：GA 不设时取 `document.referrer`。这一页是从带码的同源地址来的时候它带码 ——
 *   桌面外壳 `/pc.html?iframeUrl=<编码后的带码地址>`（iframe 与外壳同源，浏览器默认把外壳完整地址给 iframe）、card-detail 3DS 那几处
 *   同源整页跳转（从带码的 card-detail 地址跳过来）。⇒ `document.referrer` 带码时，同两处一起给 gtag 一个去掉码的 page_referrer
 *   （`referrerWithoutShareCodes`）；不带码时一个字不多给（GA 照默认，与改动前相同）。
 *   ⚠️ `document.referrer` 本身是只读的、改不了：我们管得着的只有交给 gtag 的值。页面里别的直接读它的脚本照样看得到码 ——
 *   自托管的 Umami / OpenReplay（plan §3.8 已接受）、index.html 里的 HubSpot Meetings / Google Maps / Google Pay / fatzebra
 *   （是否读 referrer 未核实，登记在 B5 changelog「发现但保留」）。要从源头断，得在 pc.html 与 index.html 设 Referrer-Policy（另议）。
 * - bug 报告的 `details.url`（App.vue `_postSubmitBug`；后端拼进 ClickUp 任务描述）。
 * 后端对同一个码只记前 6 位（`MenuOrderingLinkService::redactContextForLog`）；B6 对预览码在这两处也这么防（预览页不装 gtag / D93、
 * bug 报告剥 previewMenu / G10）。`withoutShareCodes` 与后端 `MenuTransferService::withoutShareCodes` 同名同义。
 *
 * 只 import 三个纯函数文件（不碰 store / 路由）：`utils/performance.js` 也用它。
 */
import { PREVIEW_MENU_QUERY_KEY } from './previewMenu'
import { MENU_LINK_QUERY_KEY } from './menuLink'
import { withoutQueryKeys } from '../urlQuery'

/** 两个分享码在地址里的键 */
export const SHARE_CODE_QUERY_KEYS = [PREVIEW_MENU_QUERY_KEY, MENU_LINK_QUERY_KEY]

/** 去掉地址里的两个分享码（不管几个、值对不对）；别的参数、路径、hash 逐字节不动；没有 ⇒ 原样 */
export function withoutShareCodes(href) {
  return withoutQueryKeys(href, SHARE_CODE_QUERY_KEYS)
}

/** 在地址里认「码参数」：`?` / `&` / `#` 后面紧跟两个键之一加 `=`（`menuLinkX=`、`amenuLink=` 不算） */
const SHARE_CODE_PARAM_RE = new RegExp(`[?&#](?:${SHARE_CODE_QUERY_KEYS.join('|')})=`)

/** 只为「认得出」解码：合法的 `%XX` 逐个换成那个字节（解不开的整串也照样往下认，不抛） */
function looselyDecoded(text) {
  return text.replace(/%[0-9a-fA-F]{2}/g, hex => String.fromCharCode(parseInt(hex.slice(1), 16)))
}

/** 这段文字里认得出码参数吗：原样看，再解一层、两层、三层看（嵌在别的参数里的码是编码过的） */
function mentionsShareCode(text) {
  let current = text
  for (let i = 0; i < 4; i++) {
    if (SHARE_CODE_PARAM_RE.test(current)) return true
    const decoded = looselyDecoded(current)
    if (decoded === current) return false
    current = decoded
  }
  return SHARE_CODE_PARAM_RE.test(current)
}

/** 值里嵌着带码地址的参数（桌面外壳 `iframeUrl=<编码后的地址>`）：解出来去掉码再编回去；别的参数逐字节不动 */
function withoutNestedShareCodes(href) {
  const hashAt = href.indexOf('#')
  const beforeHash = hashAt < 0 ? href : href.slice(0, hashAt)
  const hash = hashAt < 0 ? '' : href.slice(hashAt)
  const queryAt = beforeHash.indexOf('?')
  if (queryAt < 0) return href
  const parts = beforeHash.slice(queryAt + 1).split('&').map(part => {
    const eq = part.indexOf('=')
    if (eq < 0 || !mentionsShareCode(part.slice(eq + 1))) return part
    let inner
    try {
      inner = decodeURIComponent(part.slice(eq + 1))
    } catch (e) {
      return part
    }
    return part.slice(0, eq + 1) + encodeURIComponent(withoutShareCodes(inner))
  })
  return beforeHash.slice(0, queryAt + 1) + parts.join('&') + hash
}

/**
 * 上一页的地址（`document.referrer`）去掉两个分享码：
 * - 顶层参数里的直接去掉（`withoutShareCodes`；card-detail 3DS 同源整页跳转过来的那种）；
 * - 嵌在别的参数值里的（桌面外壳 `/pc.html?iframeUrl=<编码后的带码地址>`）解出来去掉、再编回去；
 * - 还认得出码（编码了两层、在 hash 里、解不开 …）⇒ 宁可多丢：整个 query 与 hash 都不要，只留 origin + 路径。
 * 没有码 ⇒ 原样（同一个字符串）。
 * @param {String} referrer
 * @returns {String}
 */
export function referrerWithoutShareCodes(referrer) {
  if (typeof referrer !== 'string' || !referrer) return referrer
  const top = withoutShareCodes(referrer)
  if (!mentionsShareCode(top)) return top
  const nested = withoutNestedShareCodes(top)
  if (!mentionsShareCode(nested)) return nested
  return nested.split(/[?#]/)[0]
}

/** 当前地址去掉两个分享码 */
function currentHrefWithoutShareCodes() {
  return typeof window !== 'undefined' && window.location ? withoutShareCodes(window.location.href) : ''
}

/**
 * 要交给 gtag 的 page_referrer：`document.referrer` 带码 ⇒ 去掉码的那个；不带码 ⇒ null（不给，GA 照默认取 `document.referrer`，与改动前相同）。
 * @returns {String|null}
 */
function referrerOverride() {
  if (typeof document === 'undefined' || typeof document.referrer !== 'string') return null
  const referrer = document.referrer
  const scrubbed = referrerWithoutShareCodes(referrer)
  return scrubbed === referrer ? null : scrubbed
}

/**
 * vue-gtag 的 `pageTrackerTemplate`：内容照 vue-gtag 1.16.1 不配模板时的默认（`page_title` = 路由名、`page_path` = 路由 path，
 * 没有 query），只多给 `page_location` = 去掉两个码的当前地址（不给的话 vue-gtag 取 `window.location.href`）；
 * `document.referrer` 带码时再多给 `page_referrer` = 去掉码的那个（B25 复核）。
 * 地址与 referrer 里都没有码时与默认逐字节相同（tests/unit/utils/menu/shareCodes.spec.js ②）。
 * @param {Object} to 路由
 */
export function gtagPageView(to) {
  const route = to || {}
  const template = {}
  if (route.name) template.page_title = route.name
  template.page_path = route.path
  template.page_location = currentHrefWithoutShareCodes()
  const referrer = referrerOverride()
  if (referrer !== null) template.page_referrer = referrer
  return template
}

/**
 * main.js 装 vue-gtag 用的选项（测试照同一份装）：`pageTrackerTemplate` 见上；`pageTrackerExcludedRoutes: ['/']`（N35-B2 第 5 轮）——
 * vue-gtag 1.16.1 在 `router.onReady` 里按那一刻的 `currentRoute` 记第一条 page_view。扫码落地时第一个导航会被我们的守卫重定向一次
 * （把进店参数写进地址），而服务器构建实际装的 vue-router 3.3.4 被重定向的那一下就算 ready（3.5 起才改成等重定向去的那一次落地），
 * 那时 currentRoute 还是起点 START（path `/`，没有名字）⇒ 多记一条 `/`。`/` 本身从不落地（第二个守卫把它整页送去官网），
 * 排除它只去掉这一条假的；落地那一页由 vue-gtag 在 onReady 里挂的 afterEach 记。两个版本都只记一条（tests/unit/router/gtagFirstNavigation.spec.js）。
 * @param {String} id GA 测量 ID
 * @returns {Object}
 */
export function gtagRouterOptions(id) {
  return {
    config: { id },
    pageTrackerTemplate: gtagPageView,
    pageTrackerExcludedRoutes: ['/']
  }
}

/**
 * 装完 vue-gtag 之后接上（main.js）：现在一次、之后每次路由切换完（地址已经换好、页面还没渲染 —— 早于新页面 mounted 里发的事件）
 * `gtag('set', { page_location: 去掉两个码的当前地址 })`，自定义事件按页面记的地址就不带码。
 * `document.referrer` 带码时同一次 set 多给 `page_referrer`（B25 复核）：`set` 在 vue-gtag 的 config 之前排进队列，之后这一页的每条事件都用它。
 * 代价（只在 referrer 带码时）：站内跳转之后的事件也记成这同一个 referrer，不再由 GA 自己算；同源 referrer 本来就不算来源，报表归因不受影响。
 * @param {Object} router vue-router 实例
 */
export function keepShareCodesOutOfGtag(router) {
  const setPageLocation = () => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
    const params = { page_location: currentHrefWithoutShareCodes() }
    const referrer = referrerOverride()
    if (referrer !== null) params.page_referrer = referrer
    window.gtag('set', params)
  }
  setPageLocation()
  if (router && typeof router.afterEach === 'function') router.afterEach(() => setPageLocation())
}
